import { useState, useMemo, useCallback } from 'react'
import { Phone, Calendar, Award, PhoneMissed } from 'lucide-react'

import type { DashboardData, FilterOptions, CallRecord } from './types'
import { mockDashboardData } from './mockData'
import { SCORE_THRESHOLDS } from './types'
import { useCallsChannel, type NewCallEvent } from '@/hooks/useCallsChannel'

import {
  KpiCard,
  TotalCallsContent,
  BookingsContent,
  QualityScoreContent,
  MissedCallsContent,
} from './components/KpiCard'
import { TopPerformersCard } from './components/TopPerformersCard'
import { CallLogFilters } from './components/CallLogFilters'
import { CallLogTable } from './components/CallLogTable'
import { MissedCallsList } from './components/MissedCallsList'

interface DashboardOverviewProps {
  initialData?: DashboardData
}

export function DashboardOverview({ initialData }: DashboardOverviewProps) {
  // Use mock data if no initial data provided or if initialData is empty
  const data = initialData && Object.keys(initialData).length > 0 ? initialData : mockDashboardData

  const [filters, setFilters] = useState<FilterOptions>(data.filters)
  const [currentPage, setCurrentPage] = useState(data.pagination.currentPage)
  const [itemsPerPage, setItemsPerPage] = useState(data.pagination.itemsPerPage)
  const [showMissedCallsModal, setShowMissedCallsModal] = useState(false)

  // State for calls to allow real-time updates
  const [calls, setCalls] = useState<CallRecord[]>(data.calls)

  // Generate CSR options dynamically from actual call data
  const csrOptions = useMemo(() => {
    const uniqueCsrs = [...new Set(calls.map(c => c.csrName))].filter(Boolean).sort()
    return [
      { value: 'any', label: 'Any' },
      ...uniqueCsrs.map(name => ({ value: name, label: name }))
    ]
  }, [calls])

  // Format time from ISO string to display format (e.g., "10:42 AM")
  const formatTime = (isoString: string): string => {
    try {
      const date = new Date(isoString)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Denver'
      })
    } catch {
      return 'N/A'
    }
  }

  // Handle new call from WebSocket
  const handleNewCall = useCallback((event: NewCallEvent) => {
    console.log('[Dashboard] New call received:', event)

    // Create a new CallRecord from the WebSocket event
    const newCall: CallRecord = {
      id: Date.now(), // Temporary ID
      time: formatTime(event.start_time),
      direction: event.direction?.toLowerCase() === 'inbound' ? 'inbound' : 'outbound',
      fromName: event.from_name || 'Unknown',
      fromPhone: event.from_phone,
      fromCompany: null,
      toPhone: event.to_phone,
      toCompany: null,
      csrName: 'Processing...', // Will be updated on refresh
      durationSec: event.duration || 0,
      result: 'not_booked', // Default, will be updated after AI analysis
      leadType: null,
      isNewOpportunity: false,
      score: 0, // Will be updated after AI analysis
      hasRecording: event.has_recording,
      summary: 'Call just ended. Analysis in progress...',
      telephonySessionId: event.telephony_session_id,
      brand: '' // Will be determined on refresh
    }

    // Prepend the new call to the list
    setCalls(prevCalls => {
      // Check if this call already exists (avoid duplicates)
      const exists = prevCalls.some(c => c.telephonySessionId === event.telephony_session_id)
      if (exists) {
        return prevCalls
      }
      return [newCall, ...prevCalls]
    })
  }, [])

  // Subscribe to WebSocket for real-time call updates
  useCallsChannel({ onNewCall: handleNewCall })

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    if (key === 'period') {
      // Reload page with new period param to fetch fresh data from server
      window.location.href = `/dashboard?period=${value}`
      return
    }
    setFilters((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page on filter change
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (count: number) => {
    setItemsPerPage(count)
    setCurrentPage(1) // Reset to first page
  }

  const handleNoteUpdate = useCallback((telephonySessionId: string, newNote: string) => {
    setCalls(prevCalls =>
      prevCalls.map(call =>
        call.telephonySessionId === telephonySessionId
          ? { ...call, notes: newNote || null }
          : call
      )
    )
  }, [])

  const { kpiMetrics, topPerformers, missedCalls, pagination } = data

  // Calculate how many missed calls were resolved (called back OR second attempt)
  const calledBackCount = useMemo(() => {
    return (missedCalls || []).filter(c => c.wasCalledBack || c.wasSecondAttempt).length
  }, [missedCalls])

  // Filter calls based on current filter state
  const filteredCalls = useMemo(() => {
    return calls.filter((call: CallRecord) => {
      // Filter by result
      if (filters.result !== 'all' && call.result !== filters.result) {
        return false
      }

      // Filter by CSR - direct name comparison (value === label in our dynamic options)
      if (filters.csr !== 'any' && call.csrName !== filters.csr) {
        return false
      }

      // Filter by score range
      if (filters.scoreRange !== 'all') {
        const score = call.score
        // Filter "analyzed" - only show calls with analysis (score > 0)
        if (filters.scoreRange === 'analyzed' && score <= 0) {
          return false
        }
        if (filters.scoreRange === 'high' && score < SCORE_THRESHOLDS.EXCELLENT) {
          return false
        }
        if (
          filters.scoreRange === 'medium' &&
          (score < SCORE_THRESHOLDS.FAIR || score >= SCORE_THRESHOLDS.EXCELLENT)
        ) {
          return false
        }
        if (filters.scoreRange === 'low' && score >= SCORE_THRESHOLDS.FAIR) {
          return false
        }
      }

      // Filter by search (CSR name, phone, summary, brand)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        const matchesCsr = call.csrName.toLowerCase().includes(searchLower)
        const matchesSummary = call.summary.toLowerCase().includes(searchLower)
        const matchesBrand = call.brand.toLowerCase().includes(searchLower)
        const matchesFromPhone = call.fromPhone?.toLowerCase().includes(searchLower) || false
        const matchesToPhone = call.toPhone?.toLowerCase().includes(searchLower) || false
        const matchesFromName = call.fromName?.toLowerCase().includes(searchLower) || false
        if (!matchesCsr && !matchesSummary && !matchesBrand && !matchesFromPhone && !matchesToPhone && !matchesFromName) {
          return false
        }
      }

      return true
    })
  }, [calls, filters])

  // Calculate pagination for filtered results
  const filteredPagination = useMemo(() => {
    const totalItems = filteredCalls.length
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
    const validCurrentPage = Math.min(currentPage, totalPages)

    return {
      ...pagination,
      currentPage: validCurrentPage,
      totalPages,
      totalItems,
      itemsPerPage,
    }
  }, [filteredCalls.length, itemsPerPage, currentPage, pagination])

  // Get calls for current page
  const paginatedCalls = useMemo(() => {
    const start = (filteredPagination.currentPage - 1) * filteredPagination.itemsPerPage
    const end = start + filteredPagination.itemsPerPage
    return filteredCalls.slice(start, end)
  }, [filteredCalls, filteredPagination.currentPage, filteredPagination.itemsPerPage])

  return (
    <div className="space-y-6">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-5 gap-4">
        {/* Total Calls */}
        <KpiCard title="Total Calls" icon={Phone}>
          <TotalCallsContent
            total={kpiMetrics.totalCalls.total}
            inbound={kpiMetrics.totalCalls.inbound}
            outbound={kpiMetrics.totalCalls.outbound}
          />
        </KpiCard>

        {/* Bookings */}
        <KpiCard title="Bookings" icon={Calendar}>
          <BookingsContent
            total={kpiMetrics.bookings.total}
            conversionRate={kpiMetrics.bookings.conversionRate}
            opportunities={kpiMetrics.bookings.opportunities}
            booked={kpiMetrics.bookings.booked}
          />
        </KpiCard>

        {/* Avg Quality Score */}
        <KpiCard title="Avg Quality Score" icon={Award}>
          <QualityScoreContent
            newOpps={kpiMetrics.avgQualityScore.newOpps}
            existing={kpiMetrics.avgQualityScore.existing}
            insurance={kpiMetrics.avgQualityScore.insurance}
          />
        </KpiCard>

        {/* Missed Calls */}
        <KpiCard title="Missed Calls" icon={PhoneMissed}>
          <MissedCallsContent
            total={kpiMetrics.missedCalls.total}
            calledBackCount={calledBackCount}
            lastMissedCall={missedCalls && missedCalls.length > 0 ? {
              fromName: missedCalls[0].fromName,
              time: missedCalls[0].time
            } : null}
            onViewAll={missedCalls && missedCalls.length > 0 ? () => setShowMissedCallsModal(true) : undefined}
          />
        </KpiCard>

        {/* Top Performers */}
        <TopPerformersCard performers={topPerformers} />
      </div>

      {/* Call Log Section */}
      <div>
        <CallLogFilters filters={filters} onFilterChange={handleFilterChange} csrOptions={csrOptions} />
        <CallLogTable
          calls={paginatedCalls}
          pagination={filteredPagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onNoteUpdate={handleNoteUpdate}
        />
      </div>

      {/* Missed Calls Modal */}
      {showMissedCallsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMissedCallsModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Missed Calls ({missedCalls?.length || 0})
              </h2>
              <button
                type="button"
                onClick={() => setShowMissedCallsModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <MissedCallsList calls={missedCalls || []} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardOverview
